/**
 * @fileoverview Chat document pickers (MOR-890)
 *
 * The composer's plus button opens one of these so the user can attach
 * documents that ground the chat's responses:
 *
 * - `JobChatDocumentPicker` — job scope. The same Finder (`FileSystem` +
 *   `buildLibraryItems`) as the Documents Library and the vouch ledger
 *   picker, including an upload button so new files can be added to the
 *   job library straight from the chat.
 * - `WorkspaceChatDocumentPicker` — workspace scope. A searchable list of
 *   recent documents grouped by job. No upload — workspace chat has no
 *   job to file an upload against.
 *
 * Both return `ChatDocPick[]` — the minimal shape the chat panel needs to
 * render composer chips and send `attachedDocumentIds`.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { FileText, Loader2, Upload } from "lucide-react";
import {
  FileSystem,
  type FileSystemItem,
} from "@/ui/composed/file-system/file-system";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/ui/composed/overlays/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { Button } from "@/ui/primitives/button";
import { CATEGORY_LABELS } from "@/components/jobs/phases/gather/constants";
import { ProcessingStatusBadge } from "@/components/jobs/phases/gather/documents/ProcessingStatusBadge";
import { useLibraryUpload } from "@/components/jobs/phases/gather/library/useLibraryUpload";
import {
  buildLibraryItems,
  type LibraryDocument,
} from "@/components/jobs/phases/gather/library/manifest";

/** A document chosen for chat grounding. */
export interface ChatDocPick {
  documentId: Id<"documents">;
  fileName: string;
  jobId: Id<"jobs">;
  /** Owning job's label — set in workspace scope for chip subtitles. */
  jobLabel?: string;
}

// ---------------------------------------------------------------------------
// Job scope — Finder + upload
// ---------------------------------------------------------------------------

export function JobChatDocumentPicker({
  jobId,
  open,
  onClose,
  onSelect,
  title = "Add files to the chat",
  description = "Select file(s) from the job library — or upload new ones — to ground Morgan's answers in them.",
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onClose: () => void;
  onSelect: (picked: ChatDocPick[]) => void;
  /** Dialog heading. Defaults to the chat-grounding copy. */
  title?: string;
  /** Dialog supporting text. Defaults to the chat-grounding copy. */
  description?: string;
}) {
  const { data: folders } = useQuery({
    ...convexQuery(api.jobs.gatherInformation.folders.queries.listFolders, {
      jobId,
    }),
    enabled: open,
  });
  const { data: documents } = useQuery({
    ...convexQuery(api.jobs.gatherInformation.documents.queries.listDocuments, {
      jobId,
    }),
    enabled: open,
  });

  const { items, metaByPath } = useMemo(
    () => buildLibraryItems(folders ?? [], documents ?? []),
    [folders, documents],
  );

  const documentById = useMemo(() => {
    const map = new Map<string, LibraryDocument>();
    for (const doc of documents ?? []) {
      map.set(doc._id, doc);
    }
    return map;
  }, [documents]);

  const [selectedDocIds, setSelectedDocIds] = useState<string[]>([]);
  useEffect(() => {
    if (open) setSelectedDocIds([]);
  }, [open]);

  const handleSelectionItemsChange = (fsItems: FileSystemItem[]) => {
    const ids: string[] = [];
    for (const item of fsItems) {
      if (item.kind !== "file") continue;
      const meta = metaByPath.get(item.path);
      if (meta?.documentId) ids.push(meta.documentId);
    }
    setSelectedDocIds(ids);
  };

  // Upload straight from the picker (into the library root) so a file that
  // isn't in the job library yet can be attached without leaving the chat.
  // The new document streams into the Finder via the reactive
  // listDocuments query.
  const { handlePickedFiles, uploadingCount } = useLibraryUpload(jobId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleConfirm = () => {
    const picked: ChatDocPick[] = [];
    for (const id of selectedDocIds) {
      const doc = documentById.get(id);
      if (!doc) continue;
      picked.push({
        documentId: doc._id as Id<"documents">,
        fileName: doc.fileName,
        jobId,
      });
    }
    if (picked.length === 0) return;
    onSelect(picked);
    onClose();
  };

  const itemColumnsHeader = (
    <>
      <span className="w-44 shrink-0">Category</span>
      <span className="w-40 shrink-0">Status</span>
    </>
  );

  const renderItemColumns = (item: FileSystemItem) => {
    const path =
      item.kind === "folder" && !item.path.endsWith("/")
        ? `${item.path}/`
        : item.path;
    const meta = metaByPath.get(path);
    const isFile = item.kind === "file";

    return (
      <>
        <span className="w-44 shrink-0 truncate text-xs text-muted-foreground">
          {isFile
            ? (CATEGORY_LABELS[meta?.category ?? ""] ?? meta?.category)
            : null}
        </span>
        <span className="flex w-40 shrink-0 items-center">
          {isFile && (
            <ProcessingStatusBadge
              extractionStatus={meta?.extractionStatus}
              classificationStatus={meta?.classificationStatus}
              indexingStatus={meta?.indexingStatus}
            />
          )}
        </span>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex h-[460px] min-h-0 flex-col">
          <FileSystem
            items={items}
            defaultView="list"
            className="min-h-0 flex-1"
            readOnly
            itemColumnsHeader={itemColumnsHeader}
            renderItemColumns={renderItemColumns}
            onSelectionItemsChange={handleSelectionItemsChange}
          />
        </div>

        <DialogFooter className="items-center">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void handlePickedFiles(files);
              e.target.value = "";
            }}
          />
          <Button
            variant="outline"
            size="sm"
            className="mr-auto gap-1.5"
            onClick={() => fileInputRef.current?.click()}
          >
            {uploadingCount > 0 ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Upload className="h-3.5 w-3.5" />
            )}
            {uploadingCount > 0
              ? `Uploading ${uploadingCount}…`
              : "Upload files"}
          </Button>
          <span className="text-xs text-muted-foreground">
            {selectedDocIds.length} selected
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleConfirm}
            disabled={selectedDocIds.length === 0}
          >
            Add{" "}
            {selectedDocIds.length > 0
              ? `${selectedDocIds.length} file${selectedDocIds.length === 1 ? "" : "s"}`
              : "files"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Workspace scope — searchable list grouped by job (no upload)
// ---------------------------------------------------------------------------

export function WorkspaceChatDocumentPicker({
  orgId,
  open,
  onClose,
  onSelect,
}: {
  orgId: Id<"organizations">;
  open: boolean;
  onClose: () => void;
  onSelect: (picked: ChatDocPick[]) => void;
}) {
  const { data: docs } = useQuery({
    ...convexQuery(
      api.agents.universal.publicQueries.listWorkspaceAttachableDocuments,
      { orgId },
    ),
    enabled: open,
  });

  const groups = useMemo(() => {
    const byJob = new Map<
      string,
      { jobId: string; jobLabel: string; docs: NonNullable<typeof docs> }
    >();
    for (const doc of docs ?? []) {
      const group = byJob.get(doc.jobId) ?? {
        jobId: doc.jobId,
        jobLabel: doc.jobLabel,
        docs: [],
      };
      group.docs.push(doc);
      byJob.set(doc.jobId, group);
    }
    return [...byJob.values()];
  }, [docs]);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg overflow-hidden p-0">
        <DialogHeader className="px-4 pt-4">
          <DialogTitle>Add files to the chat</DialogTitle>
          <DialogDescription>
            Pick documents from your jobs to ground Morgan&apos;s answers in
            them. To upload a new file, use the job&apos;s chat or its Documents
            library.
          </DialogDescription>
        </DialogHeader>
        <Command className="border-t border-border">
          <CommandInput placeholder="Search documents…" />
          <CommandList className="max-h-[380px]">
            <CommandEmpty>
              {docs === undefined ? "Loading…" : "No documents found."}
            </CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.jobId} heading={group.jobLabel}>
                {group.docs.map((doc) => (
                  <CommandItem
                    key={doc.documentId}
                    value={`${doc.fileName} ${group.jobLabel}`}
                    onSelect={() => {
                      onSelect([
                        {
                          documentId: doc.documentId,
                          fileName: doc.fileName,
                          jobId: doc.jobId,
                          jobLabel: doc.jobLabel,
                        },
                      ]);
                      onClose();
                    }}
                  >
                    <FileText className="mr-2 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {doc.fileName}
                    </span>
                    <span className="ml-2 shrink-0 text-[10px] text-muted-foreground/60">
                      {CATEGORY_LABELS[doc.category] ?? doc.category}
                    </span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
